import { z } from 'zod';
import { boundedText, maxCodePoints } from '@/lib/forms/primitives';

/**
 * Verification session (create).
 *
 * A module-level const, not a `createSchema(t)` factory: messages resolve
 * when issues are materialized, so this stays stable across language changes.
 *
 * CAPS ARE MIRRORS, not product choices —
 *   database/migrations/019_verification_tables.sql:9   title      VARCHAR(500)
 *   database/migrations/019_verification_tables.sql:10  model_name VARCHAR(200)
 * `CreateSessionRequest` (api/routes/verification.py:43-46) types them as bare
 * `str` / `str | None` with no length, so today an over-long value reaches
 * asyncpg and returns a 500 rather than a 400. Enforcing the caps here turns
 * that into visible field feedback.
 *
 * `description` deliberately carries NO length rule: the column is TEXT
 * (019_verification_tables.sql:11) and Pydantic is silent, so there is nothing
 * to mirror. Inventing a cap here would be a product decision disguised as one.
 *
 * The optional fields stay plain trimmed strings and are NOT normalised to
 * undefined here: VerificationPage already does that on the way to the API
 * (`modelName.trim() || undefined`), and two places deciding it is how the
 * empty-vs-absent contract drifts.
 */
export const sessionSchema = z.object({
  title: boundedText(500, 'validation.required', 'validation.maxLength'),
  modelName: z
    .string()
    .trim()
    .check(maxCodePoints(200, 'validation.maxLength')),
  description: z.string().trim(),
});

export type SessionFormValues = z.infer<typeof sessionSchema>;
